import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, TextField, Box, Typography, List, ListItemButton,
  ListItemText, Chip, CircularProgress, InputAdornment, Stack
} from '@mui/material';
import { Search, AutoAwesome } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import api from '../api/apiConfig';
import { useRecordDialog } from '../contexts/RecordDialogContext';
import { streamAiChat } from '../utils/aiStream';

const TYPE_LABELS = {
  suggested: 'Suggested resolutions',
  client: 'Clients',
  contact: 'Contacts',
  site: 'Sites',
  ticket: 'Tickets',
  project: 'Projects',
  asset: 'Assets',
  license: 'Subscriptions',
  quote: 'Quotes',
  invoice: 'Invoices',
  credential: 'Credentials',
  kb: 'Knowledge Base'
};

const useDebouncedValue = (value, delay = 200) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

// Heuristic: looks question-shaped if it ends with '?' or has 5+ words.
// We use this to surface the "Ask Zone" affordance — the user can still
// invoke it by clicking even when it doesn't match (the chip is also visible
// any time the query is long enough to feel like a question).
const looksLikeQuestion = (q) => {
  const trimmed = q.trim();
  if (!trimmed) return false;
  if (trimmed.endsWith('?')) return true;
  return trimmed.split(/\s+/).filter(Boolean).length >= 5;
};

// Replace [#N] tokens inside a plain text run with citation Chips. Anything
// that isn't a citation token is returned as-is so React renders it as text.
const splitTextForCitations = (text, sources, onCitationClick) => {
  const parts = text.split(/(\[#\d+\])/g);
  return parts.map((part, i) => {
    const m = part.match(/^\[#(\d+)\]$/);
    if (!m) return part;
    const n = parseInt(m[1], 10);
    const src = sources[n - 1];
    if (!src) return part;
    return (
      <Chip
        key={`cite-${i}-${n}`}
        size="small"
        label={`#${n}`}
        onClick={() => onCitationClick(src)}
        sx={{
          mx: 0.25,
          height: 18,
          fontSize: '0.65rem',
          cursor: 'pointer',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          '&:hover': { bgcolor: 'primary.dark' }
        }}
      />
    );
  });
};

// Walk a ReactMarkdown children tree, splitting string leaves on [#N] tokens
// so citations work inside any block (paragraph, list item, table cell, etc).
const walkChildrenForCitations = (children, sources, onCitationClick) => {
  return React.Children.map(children, (child) => {
    if (typeof child === 'string') {
      return splitTextForCitations(child, sources, onCitationClick);
    }
    if (React.isValidElement(child) && child.props?.children != null) {
      return React.cloneElement(
        child,
        child.props,
        walkChildrenForCitations(child.props.children, sources, onCitationClick)
      );
    }
    return child;
  });
};

// Styled markdown components. Tight margins because the answer lives inside
// a small panel; tables get the usual GFM treatment.
const buildAskMarkdownComponents = (sources, onCitationClick) => {
  const wrap = (Tag, sx) => ({ children }) => (
    <Box component={Tag} sx={sx}>
      {walkChildrenForCitations(children, sources, onCitationClick)}
    </Box>
  );
  return {
    p: wrap('p', { my: 0.75, lineHeight: 1.55 }),
    ul: wrap('ul', { pl: 2.5, my: 0.5 }),
    ol: wrap('ol', { pl: 2.5, my: 0.5 }),
    li: wrap('li', { my: 0.25, lineHeight: 1.5 }),
    h1: wrap('h3', { fontSize: '1rem', fontWeight: 700, mt: 1.5, mb: 0.5 }),
    h2: wrap('h4', { fontSize: '0.95rem', fontWeight: 700, mt: 1.5, mb: 0.5 }),
    h3: wrap('h5', { fontSize: '0.9rem', fontWeight: 700, mt: 1.25, mb: 0.5 }),
    strong: wrap('strong', { fontWeight: 700 }),
    em: wrap('em', { fontStyle: 'italic' }),
    // react-markdown v10 dropped the `inline` prop; fenced blocks now arrive
    // as <pre><code class="language-…">. Detect block code via className so
    // inline code stays in-flow and trailing punctuation in prose isn't
    // orphaned onto its own line.
    code: ({ className, children }) => {
      const isBlock = /(^|\s)language-/.test(className || '');
      if (isBlock) {
        return (
          <Box
            component="pre"
            sx={{ p: 1, my: 0.75, borderRadius: 1, bgcolor: 'action.hover', fontSize: '0.8rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowX: 'auto' }}
          >
            <Box component="code" className={className}>{children}</Box>
          </Box>
        );
      }
      return (
        <Box
          component="code"
          sx={{ px: 0.5, py: 0.1, borderRadius: 0.5, bgcolor: 'action.hover', fontSize: '0.8rem', fontFamily: 'monospace' }}
        >
          {walkChildrenForCitations(children, sources, onCitationClick)}
        </Box>
      );
    },
    pre: ({ children }) => <>{children}</>,
    table: ({ children }) => (
      <Box sx={{ overflowX: 'auto', my: 1 }}>
        <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.8rem' }}>
          {children}
        </Box>
      </Box>
    ),
    th: wrap('th', { textAlign: 'left', borderBottom: '1px solid', borderColor: 'divider', px: 1, py: 0.5, fontWeight: 700 }),
    td: wrap('td', { borderBottom: '1px solid', borderColor: 'divider', px: 1, py: 0.5, verticalAlign: 'top' }),
    blockquote: wrap('blockquote', { borderLeft: '3px solid', borderColor: 'divider', pl: 1.25, my: 0.75, color: 'text.secondary' }),
    a: ({ href, children }) => (
      <a href={href} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
        {walkChildrenForCitations(children, sources, onCitationClick)}
      </a>
    )
  };
};

// Command palette component. Open with Ctrl+K / Cmd+K. Debounced calls to
// /api/search; arrow keys + Enter to navigate; Escape closes. Results are
// flattened across groups for keyboard navigation but rendered grouped.
//
// "Ask Zone" — when the query looks like a question, a row appears at the
// top of the dropdown. Selecting it streams a RAG answer from /api/ai/ask
// over resolved tickets + KB articles, with [#N] citations linking back.
const CommandPalette = ({ open, onClose }) => {
  const { openRecord } = useRecordDialog();
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [askActive, setAskActive] = useState(false);
  const [askLoading, setAskLoading] = useState(false);
  const [askAnswer, setAskAnswer] = useState('');
  const [askSources, setAskSources] = useState([]);
  const [askError, setAskError] = useState('');
  const debouncedQuery = useDebouncedValue(query, 200);
  const inputRef = useRef(null);
  const listContainerRef = useRef(null);
  const askAbortRef = useRef(null);
  // The question the in-flight ask belongs to, so the reset effect below can
  // tell "the user typed something new" from "the user just pressed Enter".
  const askedQueryRef = useRef('');
  // An Enter pressed before the list has anything in it. Discarding it is what
  // made the palette feel broken - you typed, hit Enter, and nothing happened.
  const pendingEnterRef = useRef(false);

  // Reset state on open/close so reopening starts fresh.
  useEffect(() => {
    if (open) {
      setQuery('');
      setGroups([]);
      setActiveIndex(0);
      setAskActive(false);
      setAskAnswer('');
      setAskSources([]);
      setAskError('');
      // Reopening used to inherit a stuck askLoading, and startAsk bails out
      // while it is true - so Ask Zone stayed dead until a full page reload.
      setAskLoading(false);
      askedQueryRef.current = '';
      pendingEnterRef.current = false;
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      // Abort any in-flight ask stream when closing.
      askAbortRef.current?.abort();
      askAbortRef.current = null;
    }
  }, [open]);

  // Reset ask state when the question changes — a fresh question deserves a
  // fresh answer — but NOT for the question we have just asked.
  //
  // This keyed on debouncedQuery, which lags the input by 200ms. Press Enter
  // inside that window and the effect ran AFTER startAsk and aborted the
  // request it had just launched: the panel appeared and vanished, and no LLM
  // call ever completed. Pause longer than 200ms first and it worked, which is
  // exactly why it looked random. Keying on the live query removes the lag, and
  // the ref stops it cancelling its own ask.
  useEffect(() => {
    if (query.trim() === askedQueryRef.current) return;
    setAskActive(false);
    setAskAnswer('');
    setAskSources([]);
    setAskError('');
    setAskLoading(false);
    pendingEnterRef.current = false;
    askAbortRef.current?.abort();
    askAbortRef.current = null;
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    const q = debouncedQuery.trim();
    if (q.length < 2) {
      setGroups([]);
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    api.get('/search', { params: { q, limit: 5 } })
      .then((r) => {
        if (cancelled) return;
        setGroups(r.data?.groups || []);
        setActiveIndex(0);
      })
      .catch(() => {
        if (cancelled) return;
        setGroups([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  // Deliberately the LIVE query, not the debounced one. looksLikeQuestion is a
  // pure string test - there is no request to spare, so debouncing it bought
  // nothing and cost correctness: for 200ms after typing, the Ask Zone row did
  // not exist, so flatItems was empty and Enter selected undefined and did
  // NOTHING. The dropdown just sat there. That is the "sometimes it does
  // nothing, no LLM call" - it depended entirely on whether you paused before
  // pressing Enter.
  const showAskRow = looksLikeQuestion(query) ||
    // ...or the search found nothing. "list our open tickets" is four words
    // with no question mark, so looksLikeQuestion says no and the user got
    // "No matches" and no way forward - a dead end where asking is exactly
    // what they wanted. Keyed on the DEBOUNCED query because "we searched and
    // found nothing" is only meaningful once the search has actually run.
    (!loading && groups.length === 0 && debouncedQuery.trim().length >= 3);

  // Flat list of items in display order, used for keyboard navigation.
  // The Ask Zone row is index 0 when present.
  const flatItems = useMemo(() => {
    const out = [];
    if (showAskRow) out.push({ type: '__ask__' });
    groups.forEach(g => g.items.forEach(item => out.push(item)));
    return out;
  }, [groups, showAskRow]);

  const startAsk = () => {
    const q = query.trim();
    if (!q || askLoading) return;
    askedQueryRef.current = q;
    setAskActive(true);
    setAskAnswer('');
    setAskSources([]);
    setAskError('');
    setAskLoading(true);
    const controller = new AbortController();
    askAbortRef.current = controller;

    streamAiChat('/ai/ask', { question: q }, {
      signal: controller.signal,
      onFrame: (frame) => {
        if (frame.type === 'sources') {
          setAskSources(frame.sources || []);
        }
      },
      onContent: (delta) => {
        setAskAnswer((prev) => prev + delta);
      },
      onDone: () => {
        setAskLoading(false);
      },
      onError: (err) => {
        setAskError(err.message || 'Ask Zone failed');
        setAskLoading(false);
      },
      // An abort is not an error, but it still ends the request - without this
      // the spinner ran forever and every later ask was refused.
      onAbort: () => {
        setAskLoading(false);
      }
    });
  };

  const handleSelect = (item) => {
    if (!item) return;
    if (item.type === '__ask__') {
      startAsk();
      return;
    }
    onClose();
    openRecord({ type: item.type, id: item.id });
  };

  const handleCitationClick = (src) => {
    if (!src) return;
    onClose();
    openRecord({ type: src.type, id: src.id });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(0, flatItems.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = flatItems[activeIndex];
      if (item) {
        handleSelect(item);
      } else {
        // The debounced search has not come back yet, so there is nothing to
        // act on. Remember the keypress and honour it the moment the list
        // settles - either on the top match, or on the Ask Zone row if the
        // search finds nothing. Never silently drop it.
        pendingEnterRef.current = true;
      }
    }
  };

  // Honour an Enter that arrived before the list had loaded.
  useEffect(() => {
    if (!pendingEnterRef.current || loading) return;
    const item = flatItems[activeIndex];
    if (!item) return;
    pendingEnterRef.current = false;
    handleSelect(item);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatItems, loading, activeIndex]);

  // Keep the active row in view when navigating with the keyboard.
  useEffect(() => {
    const node = listContainerRef.current?.querySelector(`[data-cmd-index="${activeIndex}"]`);
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  let runningIndex = 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { mt: { xs: 2, sm: 6 }, borderRadius: 2, maxHeight: { xs: 'calc(100vh - 24px)', sm: 'none' }, mx: { xs: 1, sm: 'auto' } } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            inputRef={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            fullWidth
            placeholder="Search or ask a question..."
            variant="standard"
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: loading ? <CircularProgress size={16} /> : null
            }}
          />
        </Box>
        <Box ref={listContainerRef} sx={{ maxHeight: { xs: 'min(420px, 45vh)', sm: 'min(480px, 50vh)', md: 480 }, overflowY: 'auto' }}>
          {!query.trim() && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Search across clients, contacts, tickets, assets, quotes, invoices, knowledge base
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Tip: end with a <strong>?</strong> or type a full sentence to <strong>Ask Zone</strong>.
                Open from anywhere with <strong>Ctrl + K</strong>.
              </Typography>
            </Box>
          )}

          {askActive && (
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <AutoAwesome sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography variant="overline" sx={{ fontSize: '0.65rem', letterSpacing: 1, color: 'text.secondary' }}>
                  Ask Zone
                </Typography>
                {askLoading && <CircularProgress size={12} />}
              </Stack>
              {askError ? (
                <Typography variant="body2" color="error.main">{askError}</Typography>
              ) : (
                <Box sx={{ fontSize: '0.875rem', '& > *:first-of-type': { mt: 0 }, '& > *:last-child': { mb: 0 } }}>
                  {askAnswer ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkBreaks]}
                      components={buildAskMarkdownComponents(askSources, handleCitationClick)}
                    >
                      {askAnswer}
                    </ReactMarkdown>
                  ) : askLoading ? (
                    <Typography variant="body2" color="text.secondary">Thinking…</Typography>
                  ) : null}
                </Box>
              )}
              {askSources.length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1.25 }}>
                  {askSources.map((src, i) => (
                    <Chip
                      key={`${src.type}-${src.id}`}
                      size="small"
                      label={`#${i + 1} ${src.label}`}
                      onClick={() => handleCitationClick(src)}
                      sx={{ fontSize: '0.7rem', cursor: 'pointer', maxWidth: 320 }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          )}

          {showAskRow && !askActive && (
            <Box sx={{ pb: 0.5 }}>
              <List dense disablePadding>
                {(() => {
                  const idx = runningIndex;
                  runningIndex += 1;
                  const isActive = idx === activeIndex;
                  return (
                    <ListItemButton
                      data-cmd-index={idx}
                      selected={isActive}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={startAsk}
                      sx={{ px: 2, py: 1 }}
                    >
                      <AutoAwesome sx={{ fontSize: 18, mr: 1.5, color: 'primary.main' }} />
                      <ListItemText
                        primary={`Ask Zone: "${query.trim()}"`}
                        secondary="Answer from your tickets and knowledge base"
                        primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, noWrap: true }}
                        secondaryTypographyProps={{ fontSize: '0.7rem' }}
                      />
                    </ListItemButton>
                  );
                })()}
              </List>
            </Box>
          )}

          {query.trim() && !loading && groups.length === 0 && !showAskRow && !askActive && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No matches</Typography>
            </Box>
          )}
          {groups.map((group) => (
            <Box key={group.type} sx={{ pb: 0.5 }}>
              <Typography
                variant="overline"
                sx={{ display: 'block', px: 2, pt: 1.25, color: 'text.secondary', fontSize: '0.65rem', letterSpacing: 1 }}
              >
                {TYPE_LABELS[group.type] || group.type}
              </Typography>
              <List dense disablePadding>
                {group.items.map((item) => {
                  const idx = runningIndex;
                  runningIndex += 1;
                  const isActive = idx === activeIndex;
                  return (
                    <ListItemButton
                      key={`${item.type}-${item.id}`}
                      data-cmd-index={idx}
                      selected={isActive}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => handleSelect(item)}
                      sx={{ px: 2, py: 0.75 }}
                    >
                      <ListItemText
                        primary={item.label}
                        secondary={item.secondary || null}
                        primaryTypographyProps={{ fontSize: '0.875rem', noWrap: true }}
                        secondaryTypographyProps={{ fontSize: '0.75rem', noWrap: true }}
                      />
                      <Chip size="small" variant="outlined" label={TYPE_LABELS[item.type] || item.type} sx={{ ml: 1, fontSize: '0.625rem' }} />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CommandPalette;
